import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { resolveEffectiveClientId } from "utils/effectiveClientId";
import { accountMatchesSubscriptionPath } from "utils/subscriptionPath";

function mapSlaveRow(a) {
  return {
    id: a.id || `slot-${a.slot}`,
    slot: Number(a.slot),
    subscriptionPath: a.subscriptionPath || null,
    activatingDate: a.activatingDate || null,
    expiringDate: a.expiringDate || null,
    subscriptionExpired: a.subscriptionExpired === true,
    deactivated: a.deactivated === true,
    activatingDate: a.activatingDate || null,
    firstMonthRiskEnforced: a.firstMonthRiskEnforced === true,
    planId: a.planId || null,
    accountType: a.accountType || "MT5",
    accountNumber: a.accountNumber || "",
    server: a.server || "",
    copierDisplayName: a.copierDisplayName || "",
    tradeCopierAccountId: a.tradeCopierAccountId ?? null,
    tradingPasswordMasked: a.tradingPasswordMasked || "••••••••••",
    linkedToCopier: a.linkedToCopier === true,
    brokerConnected: a.brokerConnected === true,
    brokerState: a.brokerState ?? null,
    balanceDisplay: a.balanceDisplay ?? null,
    credentialsHint: a.credentialsHint ?? null,
  };
}

async function fetchSlaveList(pathQuery) {
  const q = pathQuery ? `?path=${encodeURIComponent(pathQuery)}` : "";
  const res = await fetch(apiUrl(`/api/client/copy-list-slaves${q}`), {
    credentials: "include",
    cache: "no-store",
  });
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    throw new Error(
      "Service temporarily unavailable. Please refresh the page in a moment.",
    );
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    throw new Error(
      clientSafeMessage(
        data.message,
        "Could not load your trading accounts.",
      ),
    );
  }
  const rows = Array.isArray(data.accounts) ? data.accounts : [];
  return rows.map(mapSlaveRow);
}

/**
 * Loads Duplikium slave rows for the signed-in client.
 * @param {object} session
 * @param {{ subscriptionPath?: 'path1' | 'path2' }} [options] — filter list + slots for one subscription path
 */
export function useClientDuplikiumSlaveRows(session, options = {}) {
  const subscriptionPath = options.subscriptionPath || null;
  const pollMs = options.pollMs ?? 45000;
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsLoadError, setAccountsLoadError] = useState("");

  const effectiveClientId = resolveEffectiveClientId(session);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsLoadError("");
    try {
      const allRows = await fetchSlaveList(null);
      setAllAccounts((prev) =>
        allRows.map((row) => {
          const old = (prev || []).find((p) => Number(p.slot) === Number(row.slot));
          return old?.tradingPassword
            ? { ...row, tradingPassword: old.tradingPassword }
            : row;
        }),
      );
      setAccounts((prev) => {
        const filtered = subscriptionPath
          ? allRows.filter((row) =>
              accountMatchesSubscriptionPath(row, subscriptionPath),
            )
          : allRows;
        return filtered.map((row) => {
          const old = (prev || []).find((p) => Number(p.slot) === Number(row.slot));
          return old?.tradingPassword
            ? { ...row, tradingPassword: old.tradingPassword }
            : row;
        });
      });
    } catch (e) {
      setAccountsLoadError(
        clientSafeMessage(e?.message, "Could not load your trading accounts."),
      );
    } finally {
      setAccountsLoading(false);
    }
  }, [effectiveClientId, subscriptionPath]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (!effectiveClientId || !pollMs || pollMs < 1) return undefined;
    const id = window.setInterval(() => void loadAccounts(), pollMs);
    return () => window.clearInterval(id);
  }, [effectiveClientId, loadAccounts, pollMs]);

  return {
    accounts,
    allAccounts,
    setAccounts,
    setAllAccounts,
    accountsLoading,
    accountsLoadError,
    loadAccounts,
    effectiveClientId,
    subscriptionPath,
  };
}
